// src/pages/family/children/tabs/OverviewTab.tsx
// Overview tab content for child profile

import React from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  ProfileQuickStats,
  ProfileQuickStatsSkeleton,
} from '@/features/children/components/profile/ProfileQuickStats';
import {
  DiagnosisSummaryCard,
  DiagnosisSummaryCardSkeleton,
} from '@/features/children/components/profile/DiagnosisSummaryCard';
import {
  RecentActivityCard,
  RecentActivityCardSkeleton,
} from '@/features/children/components/profile/RecentActivityCard';
import {
  UpcomingAppointmentsCard,
  UpcomingAppointmentsCardSkeleton,
} from '@/features/children/components/profile/UpcomingAppointmentsCard';
import {
  MedicationSummaryCard,
  MedicationSummaryCardSkeleton,
} from '@/features/children/components/profile/MedicationSummaryCard';
import {
  CareTeamPreviewCard,
  CareTeamPreviewCardSkeleton,
} from '@/features/children/components/profile/CareTeamPreviewCard';
import type { ChildProfileContext } from '@/features/children/components/profile/ChildProfileLayout';
import { useChildActivity } from '@/features/children/hooks/useChildProfile';

export const OverviewTab: React.FC = () => {
  const { profile, isLoading } = useOutletContext<ChildProfileContext>();

  // Fetch activity separately for more control
  const { 
    data: activities = [], 
    isLoading: activitiesLoading 
  } = useChildActivity(profile?.id, { limit: 5 });

  if (isLoading) {
    return <OverviewTabSkeleton />;
  }

  if (!profile) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Quick Stats */}
      <ProfileQuickStats stats={profile.stats} />

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left column */}
        <div className="space-y-6">
          {/* Diagnosis Summary */}
          <DiagnosisSummaryCard
            diagnoses={profile.diagnoses || []}
            childId={profile.id}
          />

          {/* Recent Activity */}
          <RecentActivityCard
            activities={activities}
            childId={profile.id}
            isLoading={activitiesLoading}
          />
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Upcoming Appointments */}
          <UpcomingAppointmentsCard
            appointments={profile.upcomingAppointments || []}
            childId={profile.id}
          />

          {/* Current Medications */}
          <MedicationSummaryCard
            medications={profile.currentMedications || []}
            childId={profile.id}
          />

          {/* Care Team Preview */}
          <CareTeamPreviewCard
            careTeam={profile.careTeam || []}
            childId={profile.id}
          />
        </div>
      </div>

      {/* Allergies & Emergency Info Banner */}
      {(profile.allergies?.length > 0 || profile.emergencyInfo) && (
        <EmergencyInfoBanner
          allergies={profile.allergies}
          emergencyInfo={profile.emergencyInfo}
          childId={profile.id}
        />
      )}
    </div>
  );
};

// Emergency info banner component
interface EmergencyInfoBannerProps {
  allergies?: string[];
  emergencyInfo?: {
    emergencyContact?: string;
    emergencyPhone?: string;
    specialInstructions?: string;
  };
  childId: string;
}

const EmergencyInfoBanner: React.FC<EmergencyInfoBannerProps> = ({
  allergies = [],
  emergencyInfo,
  childId,
}) => {
  return (
    <div className="bg-gradient-to-r from-terracotta/10 to-terracotta/5 border border-terracotta/20 rounded-2xl p-6">
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        {/* Allergies section */}
        {allergies.length > 0 && (
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-terracotta mb-2 flex items-center gap-2">
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              Allergies
            </h4>
            <div className="flex flex-wrap gap-2">
              {allergies.map((allergy, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-white rounded-full text-sm font-medium text-terracotta border border-terracotta/20"
                >
                  {allergy}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Emergency contact */}
        {emergencyInfo?.emergencyContact && (
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-terracotta mb-2 flex items-center gap-2">
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              Emergency Contact
            </h4>
            <p className="text-navy font-medium">{emergencyInfo.emergencyContact}</p>
            {emergencyInfo.emergencyPhone && (
              <a
                href={`tel:${emergencyInfo.emergencyPhone}`}
                className="text-sage hover:text-sage-dark transition-colors"
              >
                {emergencyInfo.emergencyPhone}
              </a>
            )}
          </div>
        )}

        {/* Special instructions */}
        {emergencyInfo?.specialInstructions && (
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-terracotta mb-2">
              Special Instructions
            </h4>
            <p className="text-sm text-navy-light">
              {emergencyInfo.specialInstructions}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Loading skeleton for overview tab
const OverviewTabSkeleton: React.FC = () => (
  <div className="space-y-6">
    <ProfileQuickStatsSkeleton />

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="space-y-6">
        <DiagnosisSummaryCardSkeleton />
        <RecentActivityCardSkeleton />
      </div>
      <div className="space-y-6">
        <UpcomingAppointmentsCardSkeleton />
        <MedicationSummaryCardSkeleton />
        <CareTeamPreviewCardSkeleton />
      </div>
    </div>
  </div>
);

export default OverviewTab;
